import { useTranslation } from 'react-i18next';
import { persistLocale } from '../i18n/utils';
import { SUPPORTED_LOCALES, type Locale } from '../i18n/types';

/** Human-readable labels shown on each button, written in the target language */
const LOCALE_LABELS: Record<Locale, string> = {
  en: 'EN',
  de: 'DE',
  bg: 'БГ',
};

/** Accessible switch-to descriptions written in the target language (for screen-reader users) */
const LOCALE_ARIA_LABELS: Record<Locale, string> = {
  en: 'Switch to English',
  de: 'Auf Deutsch wechseln',
  bg: 'Превключи на Български',
};

interface LanguageSwitcherProps {
  /** Currently active locale */
  currentLocale: Locale;
  /** Called when the user selects a different locale */
  onLocaleChange: (locale: Locale) => void;
}

export function LanguageSwitcher({ currentLocale, onLocaleChange }: LanguageSwitcherProps) {
  useTranslation(); // ensure component re-renders on language change

  return (
    <div
      role="group"
      aria-label="Language selection"
      className="px-4 py-3 border-t border-gray-100 flex items-center gap-1"
    >
      {SUPPORTED_LOCALES.map((locale) => {
        const isActive = locale === currentLocale;
        return (
          <button
            key={locale}
            type="button"
            aria-label={LOCALE_ARIA_LABELS[locale]}
            aria-pressed={isActive}
            onClick={() => {
              if (!isActive) {
                onLocaleChange(locale);
              }
            }}
            className={[
              'flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200',
              isActive
                ? 'bg-primary-50 text-primary-600 shadow-sm'
                : 'text-secondary-500 hover:bg-gray-50 hover:text-secondary-700',
            ].join(' ')}
          >
            {LOCALE_LABELS[locale]}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Convenience wrapper that wires the switcher to the i18n instance and localStorage.
 * Use this in Layout; use the prop-based version in tests.
 */
export function ConnectedLanguageSwitcher() {
  const { i18n } = useTranslation();
  const currentLocale = i18n.language as Locale;

  function handleLocaleChange(locale: Locale) {
    void i18n.changeLanguage(locale);
    persistLocale(locale);
    document.documentElement.lang = locale;
  }

  return (
    <LanguageSwitcher currentLocale={currentLocale} onLocaleChange={handleLocaleChange} />
  );
}
